// Feature registration. Every directory src/features/<name>/ that has a
// routes.ts contributes its routes (and menu entries) to the console; nothing
// in src/app changes when a feature package adds a screen. The shell, API and
// WebSocket layers are frozen after F1 - features plug in here.
//
//   // src/features/wall/routes.ts
//   export default defineFeatureRoutes([
//     { path: '/wall', name: 'wall', component: () => import('./WallView.vue'),
//       meta: { title: messages.lazy('navTitle'), nav: { order: 10, icon: 'wall' } } },
//
//   meta.title is a LocalText: a thunk from the feature's dictionary follows
//   the RU/EN switch (docs/design/i18n.md).
//   ])

import type { RouteRecordRaw } from 'vue-router'

export interface FeatureRoutesModule {
  default: readonly RouteRecordRaw[]
}

/** Identity function that gives a feature's routes.ts type checking. */
export function defineFeatureRoutes(routes: readonly RouteRecordRaw[]): readonly RouteRecordRaw[] {
  return routes
}

/** "features/wall/routes.ts" (any prefix) -> "wall". */
export function featureNameOf(modulePath: string): string {
  const m = /features\/([^/]+)\/routes\.ts$/.exec(modulePath)
  if (!m?.[1]) throw new Error(`not a feature routes module: ${modulePath}`)
  return m[1]
}

export interface Reserved {
  names: ReadonlySet<string>
  paths: ReadonlySet<string>
}

/**
 * Flattens the routes of all feature modules in a stable order (by feature
 * name) and refuses anything that would make navigation ambiguous: duplicate
 * names or paths (also against the shell's own routes), unnamed routes, menu
 * entries without a title or icon.
 */
export function collectFeatureRoutes(
  modules: Record<string, FeatureRoutesModule>,
  reserved: Reserved = { names: new Set(), paths: new Set() },
): RouteRecordRaw[] {
  const names = new Map<string, string>()
  const paths = new Map<string, string>()
  for (const n of reserved.names) names.set(n, 'the shell')
  for (const p of reserved.paths) paths.set(p, 'the shell')

  const out: RouteRecordRaw[] = []
  const entries = Object.entries(modules)
    .map(([path, mod]) => ({ feature: featureNameOf(path), mod }))
    .sort((a, b) => a.feature.localeCompare(b.feature))

  for (const { feature, mod } of entries) {
    const list = mod.default
    if (!Array.isArray(list)) throw new Error(`features/${feature}/routes.ts must default-export an array of routes`)
    for (const r of list as RouteRecordRaw[]) {
      if (typeof r.name !== 'string' || r.name === '') {
        throw new Error(`features/${feature}: route ${r.path} has no name`)
      }
      const nameOwner = names.get(r.name)
      if (nameOwner) throw new Error(`features/${feature}: route name "${r.name}" is already used by ${nameOwner}`)
      const pathOwner = paths.get(r.path)
      if (pathOwner) throw new Error(`features/${feature}: path "${r.path}" is already used by ${pathOwner}`)
      if (r.meta?.nav && (!r.meta.title || !r.meta.nav.icon)) {
        throw new Error(`features/${feature}: menu route "${r.name}" needs meta.title and meta.nav.icon`)
      }
      names.set(r.name, `features/${feature}`)
      paths.set(r.path, `features/${feature}`)
      out.push({ ...r, meta: { ...r.meta, feature } } as RouteRecordRaw)
    }
  }
  return out
}
